import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/contexts/TranslationContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SecondaryButton } from '@/components/ui/button-variants';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Heart, MessageCircle, Bookmark, MoreHorizontal, Camera, Search, Users, UserPlus, Copy, Flag, MessageSquare, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import SuggestedUsersSection from '@/components/Feed/SuggestedUsersSection';

import StoriesSection from '@/components/Stories/StoriesSection';
import CreatePostModal from '@/components/Profile/CreatePostModal';
import feedHeroBg from '@/assets/feed-hero-bg.jpg';

interface Post {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  profiles: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  } | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

interface SuggestedUser {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  followers_count: number;
  posts_count: number;
  pets_count: number;
}

const Feed = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [likedPosts, setLikedPosts] = useState<Set<string>>(new Set());
  const [savedPosts, setSavedPosts] = useState<Set<string>>(new Set());
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePost, setSharePost] = useState<Post | null>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [showSuggestedUsers, setShowSuggestedUsers] = useState(false);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  
  const { user, profile } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'Feed Sociale | AstroPet - Scopri i Post dei Pet';
    
    // SEO meta tags
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Scopri i post più interessanti della community AstroPet. Foto, video e storie dei pet da tutto il mondo. Unisciti alla conversazione!');
    }
    
    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', `${window.location.origin}/app/feed`);
    if (!canonical.parentElement) document.head.appendChild(canonical);
    
    fetchPosts();
    if (user) {
      checkLikedPosts();
      checkSavedPosts();
    }
  }, [user]);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('is_public', true)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const postsData = data as any || [];
      
      // Ottimizzato: batch query per i profili invece di N+1
      if (postsData.length > 0) {
        const userIds = [...new Set(postsData.map((p: any) => p.user_id).filter(Boolean))] as string[];
        
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, username, display_name, avatar_url')
          .in('user_id', userIds);

        const profilesMap = Object.fromEntries(
          (profilesData || []).map((p: any) => [p.user_id, {
            username: p.username,
            display_name: p.display_name,
            avatar_url: p.avatar_url
          }])
        );

        const postsWithProfiles = postsData.map((p: any) => ({
          ...p,
          profiles: profilesMap[p.user_id] || null
        }));

        setPosts(postsWithProfiles);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter posts based on search query
  const filteredPosts = posts.filter(post => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const content = post.content?.toLowerCase() || '';
    const username = post.profiles?.username?.toLowerCase() || '';
    const displayName = post.profiles?.display_name?.toLowerCase() || '';
    const petName = post.pets?.name?.toLowerCase() || '';
    
    return content.includes(query) || 
           username.includes(query) || 
           displayName.includes(query) || 
           petName.includes(query) ||
           query.startsWith('#') && content.includes(query);
  });


  const checkLikedPosts = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('post_likes')
        .select('post_id')
        .eq('user_id', user.id);
      if (data) {
        setLikedPosts(new Set(data.map(like => like.post_id)));
      }
    } catch (error) {
      console.error('Error checking liked posts:', error);
    }
  };

  const checkSavedPosts = async () => {
    if (!user) return;
    try {
      const { data } = await supabase
        .from('post_saves')
        .select('post_id')
        .eq('user_id', user.id);
      if (data) {
        setSavedPosts(new Set(data.map(save => save.post_id)));
      }
    } catch (error) {
      console.error('Error checking saved posts:', error);
    }
  };

  const handleLike = async (postId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!user) return;

    const isLiked = likedPosts.has(postId);

    try {
      if (isLiked) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);

        if (error) throw error;

        setLikedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });

        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, likes_count: Math.max(0, post.likes_count - 1) }
            : post
        ));
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({
            post_id: postId,
            user_id: user.id
          });

        if (error) throw error;

        setLikedPosts(prev => new Set([...prev, postId]));

        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { ...post, likes_count: post.likes_count + 1 }
            : post
        ));
      }
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleSave = async (postId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!user) return;

    const isSaved = savedPosts.has(postId);
    
    try {
      if (isSaved) {
        const { error } = await supabase
          .from('post_saves')
          .delete()
          .eq('post_id', postId)
          .eq('user_id', user.id);
        
        if (error) throw error;
        
        setSavedPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
        
        toast.success('Post rimosso dai salvati');
      } else {
        const { error } = await supabase
          .from('post_saves')
          .insert({
            post_id: postId,
            user_id: user.id
          });
        
        if (error) throw error;
        
        setSavedPosts(prev => new Set([...prev, postId]));
        toast.success('Post salvato');
      }
    } catch (error) {
      console.error('Error handling save:', error);
      toast.error('Errore nel salvare il post');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Link copiato negli appunti!');
    }).catch(() => {
      toast.error('Errore nel copiare il link');
    });
  };

  const handlePostAction = (action: string, post: Post, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    switch (action) {
      case 'report':
        toast.info('Funzionalità di segnalazione in arrivo');
        break;
      case 'copy':
        copyToClipboard(`${window.location.origin}/app/post/${post.id}`);
        break;
      case 'shareToChat':
        openShareModal(post);
        break;
    }
  };

  const fetchConversations = async () => {
    if (!user) return;
    setLoadingConversations(true);

    try {
      // First get conversations where user is a participant
      const { data: userConversations, error } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user.id)
        .is('left_at', null);

      if (error) throw error;

      const conversationIds = userConversations?.map(item => item.conversation_id) || [];

      if (conversationIds.length > 0) {
        // Get conversations details
        const { data: conversationsData, error: convError } = await supabase
          .from('conversations')
          .select('*')
          .in('id', conversationIds)
          .order('last_message_at', { ascending: false });

        if (convError) throw convError;

        // Get participants for each conversation
        const conversationsWithParticipants = await Promise.all(
          (conversationsData || []).map(async (conv) => {
            const { data: participantIds } = await supabase
              .from('conversation_participants')
              .select('user_id')
              .eq('conversation_id', conv.id)
              .is('left_at', null);

            const participants = await Promise.all(
              (participantIds || []).map(async (p) => {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('user_id, username, display_name, avatar_url')
                  .eq('user_id', p.user_id)
                  .single();

                return {
                  user_id: p.user_id,
                  profiles: profile || {
                    user_id: '',
                    username: '',
                    display_name: null,
                    avatar_url: null
                  }
                };
              })
            );

            return {
              ...conv,
              type: conv.type as 'direct' | 'group',
              participants: participants
            };
          })
        );

        setConversations(conversationsWithParticipants);
      } else {
        setConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleShareToChat = async (conversationId: string) => {
    if (!user || !sharePost) return;

    try {
      const postUrl = `${window.location.origin}/app/post/${sharePost.id}`;
      const message = `Dai un'occhiata a questo post: ${postUrl}`;

      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          content: message,
          message_type: 'text'
        });

      if (error) throw error;

      toast.success(t('feed.sharedToChat'));
      setShowShareModal(false);
      setSharePost(null);
    } catch (error) {
      console.error('Error sharing to chat:', error);
      toast.error(t('feed.shareError'));
    }
  };

  const openShareModal = (post: Post) => {
    setSharePost(post);
    setShowShareModal(true);
    fetchConversations();
  };

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${feedHeroBg})` }}
    >
      <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-24 lg:pb-6 space-y-6 sm:space-y-8">
        {/* Header Section */}
        <div className="space-y-6 animate-fade-in">
          <div className="text-center space-y-3">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold gradient-text mb-2 tracking-tight">{t('feed.title')}</h1>
            <p className="text-muted-foreground/90 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{t('feed.discover')}</p>
          </div>
          
          {/* Stories Section */}
          <div className="animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
            <StoriesSection />
          </div>

          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 animate-scale-in" style={{ animationDelay: '0.4s' }}>
            <SecondaryButton 
              size="default"
              onClick={() => setShowSuggestedUsers(!showSuggestedUsers)}
              className="flex-1 sm:max-w-xs h-12 sm:h-10 text-sm sm:text-base hover-scale transition-elastic"
            >
              <UserPlus className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="truncate">{t('feed.suggested')}</span>
            </SecondaryButton>
            <Link to="/app/explore" className="flex-1 sm:max-w-xs">
              <SecondaryButton size="default" className="w-full h-12 sm:h-10 text-sm sm:text-base hover-scale transition-elastic">
                <Users className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="truncate">{t('common.explore')}</span>
              </SecondaryButton>
            </Link>
          </div>
        </div>

        {/* Suggested Users Section - conditionally shown */}
        {showSuggestedUsers && (
          <div className="animate-fade-in">
            <SuggestedUsersSection />
          </div>
        )}

        {/* Main Feed */}
        <div className="space-y-6">
          {/* Create Post */}
          <Card className="glass-strong border-border/30 hover:border-primary/50 transition-all duration-500 hover:shadow-primary hover:scale-[1.02] animate-fade-in group">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-3 sm:gap-4">
                <Avatar className="w-10 h-10 sm:w-12 sm:h-12 ring-2 ring-primary/30 group-hover:ring-primary/60 transition-all duration-300 hover-scale">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="gradient-primary text-white font-bold text-sm sm:text-lg">
                    {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <Button 
                  variant="floating" 
                  onClick={() => setCreatePostOpen(true)} 
                  className="flex-1 min-w-0 h-10 sm:h-12 justify-start text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all duration-300 px-3 sm:px-4"
                >
                  <Camera className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-primary flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base truncate">{t('feed.shareWithPets')}</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Search Bar */}
          <Card className="glass border-border/30 hover:border-primary/30 transition-all duration-300 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <CardContent className="p-3 sm:p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 w-4 h-4" />
                <Input
                  placeholder={t('feed.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 glass border-border/20 bg-transparent hover:border-primary/30 focus:border-primary/50 transition-all duration-300 h-10 sm:h-12"
                />
              </div>
            </CardContent>
          </Card>


          {/* Posts */}
          {loading ? (
            <div className="space-y-6">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="glass border-border/20 animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <CardHeader className="p-4 sm:p-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-muted/50 rounded-full animate-pulse" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-muted/50 rounded w-24 animate-pulse" />
                        <div className="h-3 bg-muted/30 rounded w-16 animate-pulse" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0">
                    <div className="h-4 bg-muted/50 rounded w-3/4 mb-4 animate-pulse" />
                    <div className="h-48 sm:h-64 bg-muted/30 rounded-lg animate-pulse" />
                    <div className="flex items-center gap-4 mt-4">
                      <div className="h-8 bg-muted/40 rounded w-16 animate-pulse" />
                      <div className="h-8 bg-muted/40 rounded w-16 animate-pulse" />
                      <div className="h-8 bg-muted/40 rounded w-16 animate-pulse ml-auto" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
        ) : filteredPosts.length === 0 ? (
          <Card className="glass-strong border-border/30 animate-fade-in">
            <CardContent className="p-8 sm:p-12 text-center space-y-6">
              {searchQuery.trim() ? (
                <>
                  <div className="animate-float">
                    <Search className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-primary/70 mb-4" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-lg sm:text-xl font-semibold gradient-text">Nessun risultato trovato</h3>
                    <p className="text-muted-foreground/90 max-w-md mx-auto leading-relaxed">
                      Prova a cercare con termini diversi o hashtag
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="animate-float">
                    <Camera className="w-16 h-16 sm:w-20 sm:h-20 mx-auto text-primary/70 mb-4" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-lg sm:text-xl font-semibold gradient-text">{t('feed.noPosts')}</h3>
                    <p className="text-muted-foreground/90 max-w-md mx-auto leading-relaxed">
                      {t('feed.followUsers')}
                    </p>
                  </div>
                  <Link to="/app/explore">
                    <SecondaryButton className="hover-scale transition-elastic h-12 px-6 text-base">
                      <Users className="w-5 h-5 mr-2" />
                      {t('feed.findUsers')}
                    </SecondaryButton>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredPosts.map((post, index) => (
              <div key={post.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <Link to={`/app/post/${post.id}`}>
                  <Card className="glass-strong border-border/30 hover:border-primary/50 hover:shadow-primary transition-all duration-500 cursor-pointer group hover-float">
                    <CardHeader className="p-4 sm:p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Avatar className="w-10 h-10 sm:w-12 sm:h-12 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                            <AvatarImage src={post.profiles?.avatar_url || undefined} className="object-cover" />
                            <AvatarFallback className="bg-gradient-primary text-white font-medium text-sm sm:text-base">
                              {post.profiles?.display_name?.[0] || post.profiles?.username?.[0] || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-sm sm:text-base text-foreground/95 truncate">
                              {post.profiles?.display_name || post.profiles?.username}
                            </p>
                            <p className="text-xs sm:text-sm text-muted-foreground/80">
                              {formatDistanceToNow(new Date(post.created_at), {
                                addSuffix: true,
                                locale: it
                              })}
                              {post.pets && (
                                <> • {t('feed.with')} <span className="text-primary font-medium">{post.pets.name}</span></>
                              )}
                            </p>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 hover:bg-white/10 transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="glass-strong border-border/30 shadow-floating">
                            <DropdownMenuItem 
                              onClick={(e) => handlePostAction('report', post, e)}
                              className="cursor-pointer hover:bg-white/10 text-destructive"
                            >
                              <Flag className="w-4 h-4 mr-2" />
                              <span className="truncate">{t('feed.reportPost')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handlePostAction('shareToChat', post, e)}
                              className="cursor-pointer hover:bg-white/10"
                            >
                              <MessageSquare className="w-4 h-4 mr-2" />
                              <span className="truncate">{t('feed.shareToChat')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => handlePostAction('copy', post, e)}
                              className="cursor-pointer hover:bg-white/10"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              <span className="truncate">{t('feed.copyLink')}</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0 p-4 sm:p-6 space-y-4">
                      {/* Content */}
                      {post.content && (
                        <p className="text-sm sm:text-base text-foreground/90 leading-relaxed">{post.content}</p>
                      )}

                      {/* Image */}
                      {post.image_url && (
                        <div className="relative rounded-xl overflow-hidden group/image">
                          <img 
                            src={post.image_url} 
                            alt="Post content" 
                            className="w-full h-auto object-cover transition-transform duration-500 group-hover/image:scale-105" 
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/image:opacity-100 transition-opacity duration-300" />
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-border/20">
                        <div className="flex items-center space-x-4 sm:space-x-6">
                          <button
                            onClick={(e) => handleLike(post.id, e)}
                            className={`flex items-center space-x-2 transition-all duration-300 hover-scale ${
                              likedPosts.has(post.id) 
                                ? 'text-red-500 hover:text-red-600' 
                                : 'text-muted-foreground hover:text-red-500'
                            }`}
                          >
                            <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${likedPosts.has(post.id) ? 'fill-current' : ''}`} />
                            <span className="text-sm font-medium">{post.likes_count || 0}</span>
                          </button>
                          <div className="flex items-center space-x-2 text-muted-foreground">
                            <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span className="text-sm font-medium">{post.comments_count || 0}</span>
                          </div>
                        </div>
                        <button 
                          className={`transition-all duration-300 hover-scale ${
                            savedPosts.has(post.id) 
                              ? 'text-primary hover:text-primary/80' 
                              : 'text-muted-foreground hover:text-primary'
                          }`}
                          onClick={(e) => handleSave(post.id, e)}
                        >
                          <Bookmark className={`w-4 h-4 sm:w-5 sm:h-5 ${savedPosts.has(post.id) ? 'fill-current' : ''}`} />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="glass border-white/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{t('feed.shareToChat')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {loadingConversations ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-3 p-3">
                    <div className="w-10 h-10 bg-muted rounded-full animate-pulse" />
                    <div className="flex-1 space-y-1">
                      <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
                      <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">{t('feed.noChats')}</p>
                <p className="text-sm text-muted-foreground">{t('feed.startConversation')}</p>
              </div>
            ) : (
              conversations.map((conv) => {
                // Get conversation name
                const getConversationName = () => {
                  if (conv.type === 'group') {
                    return conv.name || t('feed.groupChat');
                  }
                  const otherParticipant = conv.participants?.find((p: any) => p.user_id !== user?.id);
                  return otherParticipant?.profiles?.display_name || otherParticipant?.profiles?.username || 'Chat';
                };

                const getConversationAvatar = () => {
                  if (conv.type === 'group') {
                    return conv.avatar_url;
                  }
                  const otherParticipant = conv.participants?.find((p: any) => p.user_id !== user?.id);
                  return otherParticipant?.profiles?.avatar_url;
                };

                return (
                  <div 
                    key={conv.id} 
                    onClick={() => handleShareToChat(conv.id)} 
                    className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={getConversationAvatar() || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getConversationName().charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-white">
                        {getConversationName()}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {conv.type === 'direct' ? t('feed.directChat') : t('feed.group')}
                      </p>
                    </div>
                    <Send className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Post Modal */}
      <CreatePostModal
        open={createPostOpen}
        onOpenChange={setCreatePostOpen}
        onSuccess={() => {
          fetchPosts();
          setCreatePostOpen(false);
        }}
      />
      </div>
    </div>
  );
};

export default Feed;